import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import groupBy from 'lodash/groupBy';
import magnifyingGlass from 'open-iconic/svg/magnifying-glass.svg';
import { Menu, MenuItem, Typeahead } from 'react-bootstrap-typeahead';

import { noop } from 'lib/noop';
import { IconButton } from 'ui/Components';
import { makeId } from 'utils/selectors';

import Highlighter from './Highlighter';
import styles from './Bar.css';

/**
 * render a search bar input with typeahead abilities
 */
export default class Bar extends Component {
  static propTypes = {
    addFilter: propTypes.func.isRequired,
    componentTypeSelected: propTypes.string,
    context: propTypes.string.isRequired,
    dynamicSelected: propTypes.string,
    endDateSelected: propTypes.string,
    filters: propTypes.arrayOf(
      propTypes.shape({
        k: propTypes.string,
        v: propTypes.oneOfType([propTypes.string, propTypes.array])
      })
    ),
    getSearchResults: propTypes.func.isRequired,
    getSearchSuggestions: propTypes.func.isRequired,
    isResultsLoading: propTypes.bool.isRequired,
    isSuggestionsLoading: propTypes.bool.isRequired,
    maxSuggestionsPerGroup: propTypes.number,
    minSearchInputLength: propTypes.number,
    onSearch: propTypes.func.isRequired,
    startDateSelected: propTypes.string,
    storeResultPageNum: propTypes.func,
    suggestions: propTypes.arrayOf(
      propTypes.shape({
        text: propTypes.string,
        key: propTypes.string,
        val: propTypes.string
      })
    ).isRequired,
    userIdSelected: propTypes.string
  };

  static defaultProps = {
    componentTypeSelected: '',
    dynamicSelected: 'false',
    endDateSelected: '',
    filters: [],
    maxSuggestionsPerGroup: 5,
    minSearchInputLength: 2,
    startDateSelected: '',
    storeResultPageNum: noop,
    userIdSelected: ''
  };

  constructor(props) {
    super(props);

    this.state = {
      hasRequested: false,
      hasSearched: false,
      searchText: ''
    };

    this.input = null;

    this.clearInput = this.clearInput.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.renderMenu = this.renderMenu.bind(this);
    this.setInputRef = this.setInputRef.bind(this);
    this.viewAllResults = this.viewAllResults.bind(this);
  }

  /**
   * update internal state when props change
   * @param {object} nextProps - nextProps
   * @param {object} prevState - prevState
   * @returns {object} object with which to update the internal state
   */
  static getDerivedStateFromProps(nextProps, prevState) {
    return {
      // search previously requested and no longer loading means it is done
      hasSearched: prevState.hasRequested && !nextProps.isSuggestionsLoading
    };
  }

  /**
   * request search suggestions when input text changes
   * @param {string} text - the input text
   */
  handleInputChange(text) {
    const { filters, getSearchSuggestions, minSearchInputLength } = this.props;

    this.setState({
      searchText: text.toString()
    });

    const shouldRequest = text.length >= minSearchInputLength;
    if (shouldRequest) {
      getSearchSuggestions(text, filters);
    }
    this.setState({ hasRequested: shouldRequest });
  }

  handleKeyPress(e) {
    const { addFilter, storeResultPageNum } = this.props;
    const text = this.state.searchText;
    if (e.key === 'Enter') {
      addFilter('Name', text);
      storeResultPageNum(1);
      this.clearInput();
    }
  }

  /**
   * set a ref to the search input dom element
   * @param {Element} typeahead - a ref to the input element
   */
  setInputRef(typeahead) {
    if (!this.input) {
      this.input = typeahead.getInstance();
    }
  }

  /**
   * clear search input
   */
  clearInput() {
    if (this.input) {
      this.input.clear();
    }
  }

  viewAllResults(tag, text) {
    const { addFilter, storeResultPageNum } = this.props;
    if (tag === 'BrandName') {
      addFilter('BrandName-keyword', text);
    } else {
      addFilter(tag, text);
    }
    storeResultPageNum(1);
  }

  /**
   * render the typeahead menu with a list suggestions grouped by type
   * @param {Array} suggestions - suggestions to list
   * @param {object} menuProps - additional props for the menu
   * @returns {React.Component} the typeahead menu
   */
  renderMenu(suggestions, menuProps) {
    const { addFilter, maxSuggestionsPerGroup, storeResultPageNum } = this.props;

    const { searchText } = this.state;

    // group suggestions by tag text
    const grouped = groupBy(suggestions, s => s.text);
    let itemPosition = 0;

    return (
      <Menu id="searchMenu" className={styles.menu} {...menuProps}>
        {Object.keys(grouped)
          .sort()
          .map((tag, tagIndex) => (
            <Fragment key={tag}>
              {!!tagIndex && <hr />}
              <div className={styles.tagAndViewAllResults}>
                <span className="dropdown-header">{tag}</span>
                <button
                  type="button"
                  onClick={() => {
                    this.viewAllResults(tag, searchText);
                    this.clearInput();
                  }}
                >
                  view all results
                </button>
              </div>
              {grouped[tag].slice(0, maxSuggestionsPerGroup).map(suggestion => {
                const { key, val } = suggestion;
                const item = (
                  <span className="w-100" key={`${key}-${val}`}>
                    <MenuItem
                      option={suggestion}
                      position={itemPosition}
                      data-test-id={makeId({
                        component: this.constructor.name,
                        name: 'suggestion',
                        value: val
                      })}
                      id={val}
                      onClick={() => {
                        addFilter(key, val);
                        storeResultPageNum(1);
                        this.clearInput();
                      }}
                    >
                      <Highlighter
                        className="d-block text-truncate"
                        search={menuProps.text}
                        text={suggestion.val}
                      />
                    </MenuItem>
                  </span>
                );
                itemPosition++;
                return item;
              })}
            </Fragment>
          ))}
      </Menu>
    );
  }

  render() {
    const {
      context,
      isResultsLoading,
      isSuggestionsLoading,
      minSearchInputLength,
      onSearch,
      suggestions
    } = this.props;

    const { hasSearched } = this.state;

    // Typeahead library will not allow a falsy value in emptyLabel prop
    const emptyLabelValue = hasSearched ? 'No matches found.' : ' ';

    return (
      <div className="d-flex position-relative" data-test-id={`bar-${context}`}>
        <Typeahead
          className="w-100"
          emptyLabel={emptyLabelValue}
          id="typeahead"
          isLoading={isSuggestionsLoading || isResultsLoading}
          labelKey="val"
          minLength={minSearchInputLength}
          onKeyDown={this.handleKeyPress}
          onInputChange={this.handleInputChange}
          options={suggestions}
          placeholder="Search by keyword..."
          ref={this.setInputRef}
          renderMenu={this.renderMenu}
        />
        <IconButton
          alt="search icon"
          className={styles.icon}
          icon={magnifyingGlass}
          noMargin
          onClick={onSearch}
          rem={1.5}
        />
      </div>
    );
  }
}
